import { z } from "zod";
import {
  instantSchema,
  invoiceSchema,
  legalDocumentKindSchema,
  legalDocumentSchema,
  localDateSchema,
  tenantContactSchema,
} from "./schemas";

/*
 * Runtime schemas of the platform API (`/platform/v1`, operator area of the web app, F-69). They
 * mirror `backend/openapi-platform.json`; like the other schemas they are not strict, so additive
 * server changes do not break older clients.
 */

/** Roles of the operator's staff (spec 18.3 no. 6). */
export const platformRoleSchema = z.enum(["PlatformAdmin", "PlatformSupport"]);

/** A role of the operator's staff. */
export type PlatformRole = z.infer<typeof platformRoleSchema>;

/** `GET /platform/v1/me`: the platform roles of the signed-in person; unknown roles stay text. */
export const platformAccountSchema = z.object({
  roles: z.array(z.string()),
});

/** Contract status of a tenant in the catalog. */
export const catalogTenantStatusSchema = z.enum([
  "Trial",
  "Active",
  "PastDue",
  "Suspended",
  "Terminated",
  "ExportWindow",
  "Deleted",
]);

/** Contract status of a tenant in the catalog. */
export type CatalogTenantStatus = z.infer<typeof catalogTenantStatusSchema>;

/** Where the business data of a tenant is stored. */
export const tenantIsolationSchema = z.enum(["Pool", "Silo"]);

/** Protection need of a tenant's data (spec 18.5). */
export const protectionNeedSchema = z.enum(["High", "VeryHigh"]);

/** Protection need of a tenant with the answers of the questionnaire (MT-18). */
export const protectionSchema = z.object({
  publicSector: z.boolean(),
  healthCare: z.boolean(),
  criticalInfrastructure: z.boolean(),
  employees: z.number().int(),
  need: protectionNeedSchema,
  recommendedIsolation: tenantIsolationSchema,
});

/** Protection need of a tenant. */
export type Protection = z.infer<typeof protectionSchema>;

/** A tenant in the catalog (`GET /platform/v1/tenants`). */
export const catalogTenantSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: catalogTenantStatusSchema,
  planId: z.string(),
  isolation: tenantIsolationSchema,
  databaseKey: z.string().nullable(),
  movingToDatabaseKey: z.string().nullable(),
  protection: protectionSchema.nullable(),
  identityOrganizationId: z.string().nullable(),
  createdAt: instantSchema,
  statusChangedAt: instantSchema,
  /** Last day of the trial; missing in answers of servers before F-69. */
  trialEndsOn: localDateSchema.nullable().optional(),
});

/** A tenant in the catalog. */
export type CatalogTenant = z.infer<typeof catalogTenantSchema>;

/** `GET /platform/v1/tenants`. */
export const catalogTenantsSchema = z.array(catalogTenantSchema);

/** Why the status of a tenant changed. */
export const lifecycleReasonSchema = z.enum([
  "OperatorDecision",
  "SubscriptionStarted",
  "PaymentFailed",
  "PaymentReceived",
  "DunningPeriodExpired",
  "ContractEnded",
  "Reactivated",
  "ExportWindowExpired",
]);

/** A status change of a tenant (`GET /platform/v1/tenants/{id}/lifecycle-events`). */
export const lifecycleEventSchema = z.object({
  id: z.uuid(),
  fromStatus: catalogTenantStatusSchema,
  toStatus: catalogTenantStatusSchema,
  reason: lifecycleReasonSchema,
  occurredAt: instantSchema,
  actorSubject: z.string(),
});

/** A status change of a tenant. */
export type LifecycleEvent = z.infer<typeof lifecycleEventSchema>;

/** `GET /platform/v1/tenants/{id}/lifecycle-events`. */
export const lifecycleEventsSchema = z.array(lifecycleEventSchema);

/** Measured use of a tenant in one month (`GET /platform/v1/tenants/{id}/usage`). */
export const usageSchema = z.object({
  month: localDateSchema,
  activeEmployees: z.number().int(),
  employees: z.number().int(),
});

/** `GET /platform/v1/tenants/{id}/usage`. */
export const usagesSchema = z.array(usageSchema);

/** `GET /platform/v1/tenants/{id}/contacts`. */
export const platformContactsSchema = z.array(tenantContactSchema);

/** A decision of a tenant on a legal document (`GET /platform/v1/tenants/{id}/legal-acceptances`). */
export const legalAcceptanceSchema = z.object({
  id: z.uuid(),
  legalDocumentId: z.uuid(),
  kind: legalDocumentKindSchema,
  version: z.string(),
  decision: z.enum(["Accepted", "Declined"]),
  decidedByEmployeeId: z.uuid(),
  decidedAt: instantSchema,
});

/** `GET /platform/v1/tenants/{id}/legal-acceptances`. */
export const legalAcceptancesSchema = z.array(legalAcceptanceSchema);

/** A current plan version (`GET /platform/v1/plans`). */
export const platformPlanSchema = z.object({
  code: z.string(),
  name: z.string(),
  isPublic: z.boolean(),
  validFrom: localDateSchema,
  basePriceCents: z.number().int(),
  pricePerActiveEmployeeCents: z.number().int(),
  setupFeeCents: z.number().int(),
  currency: z.string(),
  employeeLimit: z.number().int().nullable(),
  entitlements: z.array(z.string()),
});

/** A current plan version. */
export type PlatformPlan = z.infer<typeof platformPlanSchema>;

/** `GET /platform/v1/plans`. */
export const platformPlansSchema = z.array(platformPlanSchema);

/** A configured business database (`GET /platform/v1/databases`). */
export const databaseSchema = z.object({
  key: z.string(),
  tenants: z.number().int(),
  isConfigured: z.boolean(),
});

/** `GET /platform/v1/databases`. */
export const databasesSchema = z.array(databaseSchema);

/** `GET /platform/v1/database-options`: what the operator area may do with databases (F-94). */
export const databaseOptionsSchema = z.object({
  ownDatabasesOnServer: z.boolean(),
});

/** State of a self-service registration. */
export const signupStatusSchema = z.enum(["PendingVerification", "Completed", "Duplicate"]);

/** A registration (`GET /platform/v1/signups`). */
export const signupSchema = z.object({
  id: z.uuid(),
  companyName: z.string(),
  postalCode: z.string(),
  contactEmail: z.string(),
  status: signupStatusSchema,
  requestedAt: instantSchema,
  completedAt: instantSchema.nullable(),
  tenantId: z.uuid().nullable(),
  lastError: z.string().nullable(),
});

/** A registration. */
export type Signup = z.infer<typeof signupSchema>;

/** `GET /platform/v1/signups`. */
export const signupsSchema = z.array(signupSchema);

/** Steps of an onboarding, in their order (T-103). */
export const onboardingStepSchema = z.enum([
  "Started",
  "OrganizationCreated",
  "AdministratorCreated",
  "TenantProvisioned",
  "Registered",
]);

/** A step of an onboarding. */
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

/** State of an onboarding (`GET /platform/v1/onboardings/{tenantId}`). */
export const onboardingSchema = z.object({
  tenantId: z.uuid(),
  tenantName: z.string(),
  status: z.enum(["InProgress", "Completed", "RolledBack"]),
  step: onboardingStepSchema,
  organizationId: z.string().nullable(),
  administratorUserId: z.string().nullable(),
  invitationSentAt: instantSchema.nullable(),
  lastError: z.string().nullable(),
  startedAt: instantSchema,
  updatedAt: instantSchema,
});

/** State of an onboarding. */
export type Onboarding = z.infer<typeof onboardingSchema>;

/** Protocol of the identity provider of a customer. */
export const federationProtocolSchema = z.enum(["Oidc", "Saml"]);

/** Single sign-on of a tenant (`GET /platform/v1/tenants/{id}/single-sign-on`). */
export const singleSignOnSchema = z.object({
  tenantId: z.uuid(),
  protocol: federationProtocolSchema,
  metadataUrl: z.string(),
  clientId: z.string().nullable(),
  domains: z.array(z.string()),
  providerAlias: z.string(),
  configuredAt: instantSchema,
});

/** Single sign-on of a tenant. */
export type SingleSignOn = z.infer<typeof singleSignOnSchema>;

/** An emergency access as operator support sees it. */
export const platformEmergencyAccessSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  status: z.string(),
  hours: z.number().int(),
  requestedAt: instantSchema,
  validUntil: instantSchema.nullable(),
});

/** `GET /platform/v1/tenants/{id}/emergency-accesses`. */
export const platformEmergencyAccessesSchema = z.array(platformEmergencyAccessSchema);

/** `GET /platform/v1/tenants/{id}/invoices` (F-70): invoices and credit notes, newest first. */
export const platformInvoicesSchema = z.array(invoiceSchema);

/** `POST /platform/v1/tenants/{id}/vat-id:check`: the confirmation of VIES. */
export const vatIdCheckSchema = z.object({
  isValid: z.boolean(),
  reference: z.string().nullable(),
  checkedAt: instantSchema,
});

/** The confirmation of VIES for a VAT number. */
export type VatIdCheck = z.infer<typeof vatIdCheckSchema>;

/** `POST /platform/v1/payments:reconcile`: the comparison with the payment provider. */
export const paymentReconciliationSchema = z.object({
  checked: z.number().int(),
  mismatches: z.array(z.object({ tenantId: z.uuid(), findings: z.array(z.string()) })),
  failed: z.array(z.uuid()),
  webhooksDeleted: z.number().int(),
});

/** The result of a comparison with the payment provider. */
export type PaymentReconciliation = z.infer<typeof paymentReconciliationSchema>;

/** `GET /platform/v1/legal-documents` (F-70): every published version. */
export const platformLegalDocumentsSchema = z.array(legalDocumentSchema);

/** Kind of an announced change of a subprocessor. */
export const subprocessorChangeKindSchema = z.enum(["Added", "Changed", "Removed"]);

/** An announced change of a subprocessor (T-131). */
export const subprocessorEntrySchema = z.object({
  announcementId: z.uuid(),
  subprocessorId: z.uuid(),
  kind: subprocessorChangeKindSchema,
  name: z.string(),
  purpose: z.string(),
  dataCategories: z.string(),
  location: z.string(),
  transferSafeguard: z.string().nullable(),
  effectiveFrom: localDateSchema,
  announcedAt: instantSchema,
});

/** An announced change of a subprocessor. */
export type SubprocessorEntry = z.infer<typeof subprocessorEntrySchema>;

/** `GET /platform/v1/subprocessors`: the register with the pending changes and the history. */
export const subprocessorRegisterSchema = z.object({
  current: z.array(subprocessorEntrySchema),
  upcoming: z.array(subprocessorEntrySchema),
  history: z.array(subprocessorEntrySchema),
});

/** The register of subprocessors. */
export type SubprocessorRegister = z.infer<typeof subprocessorRegisterSchema>;

/** Delivery state of the notice of an announcement to one tenant. */
export const subprocessorNoticeStatusSchema = z.enum(["Pending", "Sent", "NoRecipient"]);

/** The notice of an announcement to one tenant. */
export const subprocessorNoticeSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  recipient: z.string().nullable(),
  status: subprocessorNoticeStatusSchema,
  attempts: z.number().int(),
  lastError: z.string().nullable(),
  sentAt: instantSchema.nullable(),
});

/** `GET /platform/v1/subprocessor-announcements/{id}/notices`. */
export const subprocessorNoticesSchema = z.array(subprocessorNoticeSchema);

/** `POST /platform/v1/tenants/{id}/security-incident-notices`: the queued message. */
export const securityIncidentNoticeSchema = z.object({
  emailId: z.uuid(),
  status: z.enum(["Pending", "Sent", "Failed"]),
  queuedAt: instantSchema,
});

/** Rows deleted from or copied into one table. */
export const tableRowsSchema = z.object({
  table: z.string(),
  rows: z.number().int(),
});

/** `GET /platform/v1/tenants/{id}/offboarding`: the export window and the deletion report. */
export const offboardingSchema = z.object({
  tenantId: z.uuid(),
  status: z.enum(["Scheduled", "Deleted"]),
  scheduledAt: instantSchema,
  deleteAfter: instantSchema,
  deletedAt: instantSchema.nullable(),
  report: z.array(tableRowsSchema),
});

/** The export window of a tenant and its deletion report. */
export type Offboarding = z.infer<typeof offboardingSchema>;

/** `POST /platform/v1/tenants/{id}/database:move-to-silo`: the report of the move. */
export const databaseMoveSchema = z.object({
  tenantId: z.uuid(),
  databaseKey: z.string(),
  copied: z.array(tableRowsSchema),
  purged: z.array(tableRowsSchema),
});

/** The report of a move into an own database. */
export type DatabaseMove = z.infer<typeof databaseMoveSchema>;

/** `POST /platform/v1/tenants/{id}/keys:rotate`: the new key version. */
export const keyRotationSchema = z.object({
  version: z.number().int(),
  resealed: z.number().int(),
});

/** An entry of the platform audit log (`GET /platform/v1/audit-log`, F-71). */
export const platformAuditEntrySchema = z.object({
  id: z.uuid(),
  occurredAt: instantSchema,
  actorSubject: z.string(),
  action: z.string(),
  tenantId: z.uuid().nullable(),
  comment: z.string().nullable(),
  details: z.string(),
});

/** An entry of the platform audit log. */
export type PlatformAuditEntry = z.infer<typeof platformAuditEntrySchema>;

/** `GET /platform/v1/audit-log`: a page of the platform audit log, newest first (F-71). */
export const platformAuditPageSchema = z.object({
  entries: z.array(platformAuditEntrySchema),
  hasMore: z.boolean(),
  next: z.string().nullable(),
});

/** A page of the platform audit log. */
export type PlatformAuditPage = z.infer<typeof platformAuditPageSchema>;

/** Where a plan version stands on the current day. */
export const planVersionStateSchema = z.enum(["Past", "Current", "Scheduled"]);

/** Where a plan version stands on the current day. */
export type PlanVersionState = z.infer<typeof planVersionStateSchema>;

/** A version of a plan with its state today (`GET /platform/v1/plan-versions`, F-71). */
export const planVersionSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  isPublic: z.boolean(),
  validFrom: localDateSchema,
  basePriceCents: z.number().int(),
  pricePerActiveEmployeeCents: z.number().int(),
  setupFeeCents: z.number().int(),
  currency: z.string(),
  employeeLimit: z.number().int().nullable(),
  entitlements: z.array(z.string()),
  state: planVersionStateSchema,
});

/** A version of a plan with its state today. */
export type PlanVersion = z.infer<typeof planVersionSchema>;

/** `GET /platform/v1/plan-versions`. */
export const planVersionsSchema = z.array(planVersionSchema);

/** A difference between the plan catalog and the prices that can be bought online. */
export const planPriceFindingSchema = z.enum([
  "PublicWithoutPrice",
  "PriceForPlanOnRequest",
  "PriceWithoutPlan",
  "PerEmployeePriceMissing",
  "SetupPriceMissing",
  "SetupPriceWithoutFee",
  "BasePriceDiffers",
  "PerEmployeePriceDiffers",
  "SetupPriceDiffers",
  "ProviderPriceUnavailable",
]);

/** A difference between the plan catalog and the prices that can be bought online. */
export type PlanPriceFinding = z.infer<typeof planPriceFindingSchema>;

/**
 * The comparison of a plan with `Payments:Prices` (`GET /platform/v1/plan-prices`, F-71): since F-96 with the
 * prices per active employee and for the setup, and the amounts at the payment provider.
 */
export const planPriceCheckSchema = z.object({
  code: z.string(),
  priceId: z.string().nullable(),
  isPublic: z.boolean().nullable(),
  findings: z.array(planPriceFindingSchema),
  /** Price per active employee (F-96); `null` without one and from servers before F-96. */
  perEmployeePriceId: z.string().nullable().default(null),
  /** One-time setup price (F-96); `null` without one and from servers before F-96. */
  setupPriceId: z.string().nullable().default(null),
});

/** The comparison of a plan with the prices that can be bought online. */
export type PlanPriceCheck = z.infer<typeof planPriceCheckSchema>;

/** `GET /platform/v1/plan-prices`. */
export const planPriceChecksSchema = z.array(planPriceCheckSchema);

/** The trial of a tenant (`GET /platform/v1/tenants/{id}/trial`, F-71). */
export const trialSchema = z.object({
  tenantId: z.uuid(),
  status: catalogTenantStatusSchema,
  trialEndsOn: localDateSchema.nullable(),
  regularEndsOn: localDateSchema,
  extensions: z.number().int(),
  extensionsLeft: z.number().int(),
  latestEndsOn: localDateSchema,
});

/** The trial of a tenant with the room left for extensions. */
export type Trial = z.infer<typeof trialSchema>;

/** State of one kind of background job over all tenants. */
export const backgroundJobKindSchema = z.object({
  kind: z.string(),
  lastSucceededAt: instantSchema.nullable(),
  lastFailedAt: instantSchema.nullable(),
  failed: z.number().int(),
  retrying: z.number().int(),
});

/** State of one kind of background job. */
export type BackgroundJobKind = z.infer<typeof backgroundJobKindSchema>;

/**
 * Last run of a step of a scheduled worker (F-86); `lastRun` is `Succeeded`, `Failed`, `Skipped`
 * (a step it needs failed or was skipped, F-103) or `NotRun` (never run, or the run ended before
 * it), a later server may send another value.
 */
export const workerStepSchema = z.object({
  step: z.string(),
  lastRun: z.string(),
  lastStartedAt: instantSchema.nullable(),
  lastFinishedAt: instantSchema.nullable(),
  lastSucceededAt: instantSchema.nullable(),
  lastFailedAt: instantSchema.nullable(),
  lastError: z.string().nullable(),
});

/** Last run of a step of a scheduled worker. */
export type WorkerStep = z.infer<typeof workerStepSchema>;

/** Last run of a scheduled worker of the API, with its steps in the order they run (F-86). */
export const workerRunSchema = z.object({
  worker: z.string(),
  lastStartedAt: instantSchema,
  lastFinishedAt: instantSchema,
  lastSucceededAt: instantSchema.nullable(),
  lastFailedAt: instantSchema.nullable(),
  lastError: z.string().nullable(),
  steps: z.array(workerStepSchema),
});

/** Last run of a scheduled worker. */
export type WorkerRun = z.infer<typeof workerRunSchema>;

/** Migration state of a database; the status is `Current`, `Outdated` or `Unreachable`. */
export const schemaStateSchema = z.object({
  database: z.string(),
  status: z.string(),
  pendingMigrations: z.number().int(),
});

/** Migration state of a database. */
export type SchemaState = z.infer<typeof schemaStateSchema>;

/** Review of a pending legislative procedure of the scope register. */
export const legalReviewItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  checkedOn: localDateSchema,
  nextReviewBy: localDateSchema,
  isOverdue: z.boolean(),
  isUnchanged: z.boolean(),
});

/** Review of a pending legislative procedure. */
export type LegalReviewItem = z.infer<typeof legalReviewItemSchema>;

/** `GET /platform/v1/system-status`: the system status without personal data (F-71). */
export const systemStatusSchema = z.object({
  checkedAt: instantSchema,
  jobWindowStart: instantSchema,
  jobs: z.array(backgroundJobKindSchema),
  unreachableJobDatabases: z.number().int(),
  workers: z.array(workerRunSchema),
  emailOutbox: z.object({
    pending: z.number().int(),
    failed: z.number().int(),
    oldestPendingSince: instantSchema.nullable(),
  }),
  schemas: z.array(schemaStateSchema),
  legalReview: z.object({
    registerVersion: z.string(),
    registerCheckedOn: localDateSchema,
    items: z.array(legalReviewItemSchema),
  }),
});

/** The system status. */
export type SystemStatus = z.infer<typeof systemStatusSchema>;

/** An account of operator staff (F-71); unknown roles stay text. */
export const staffAccountSchema = z.object({
  subject: z.string(),
  username: z.string(),
  roles: z.array(z.string()),
  enabled: z.boolean(),
  hasSecondFactor: z.boolean(),
  consoleUrl: z.string().nullable(),
});

/** An account of operator staff. */
export type StaffAccount = z.infer<typeof staffAccountSchema>;

/** `GET /platform/v1/accounts`: the accounts of operator staff, ordered by user name (F-71). */
export const staffAccountListSchema = z.object({
  consoleUrl: z.string().nullable(),
  accounts: z.array(staffAccountSchema),
});

/** The accounts of operator staff. */
export type StaffAccountList = z.infer<typeof staffAccountListSchema>;
